import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { firestore, firebaseAuth } from '../firebase';
import { collection, query, where, orderBy, getDocs, doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useToast } from '../Context/ToastContext';
import './OrdersScreen.css';

function OrdersScreen() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [cancelingItemId, setCancelingItemId] = useState(null);
  const [isBulkCanceling, setIsBulkCanceling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showBulkCancelConfirm, setShowBulkCancelConfirm] = useState(false);
  const [pendingCancelItem, setPendingCancelItem] = useState(null);
  
  const userSignin = useSelector((state) => state.userSignin) || {};
  const { userInfo } = userSignin;
  const { error: toastError, success: toastSuccess } = useToast();

  // Determine payment status from order context
  // Rules:
  // - If order is Cancelled -> Not Paid
  // - COD -> Paid only after Delivered, otherwise Not Paid (displayed as Pending)
  // - Prepaid (Razorpay/others) -> Paid only if we have a transaction marker, else Not Paid
  const determinePaymentStatusFromOrder = (order) => {
    const status = (order?.Order_Status || '').toLowerCase();
    const method = (order?.Payment_Method || '').toLowerCase();
    const isCOD = method.includes('cod');
    const hasTxn = Array.isArray(order?.items) && order.items.some(i => !!(i?.CustTransaction_No || i?.CustUTR_No));
    if (status === 'cancelled') return 'Not Paid';
    if (isCOD) return status === 'delivered' ? 'Paid' : 'Not Paid';
    return hasTxn ? 'Paid' : 'Not Paid';
  };

  // Helper: compute aggregated order status and payment status from items
  const aggregateOrderMeta = (items) => {
    const priority = { 'delivered': 4, 'shipped': 3, 'packed': 2, 'ordered': 1, 'cancelled': 0 };
    let best = null;
    items.forEach(it => {
      const st = (it.Order_Status || '').toLowerCase();
      if (best == null || (priority[st] ?? -1) > (priority[best] ?? -1)) best = st;
    });
    const representative = items[0] || {};
    const orderStatus = best ? best.charAt(0).toUpperCase() + best.slice(1) : representative.Order_Status || 'Ordered';
    const orderLike = { Order_Status: orderStatus, Payment_Method: representative.Payment_Method, items };
    const paymentStatus = determinePaymentStatusFromOrder(orderLike);
    return { orderStatus, paymentStatus };
  };

  // Helper: update parent ORDERS/{orderId} doc
  const updateParentOrderDoc = async (orderId) => {
    try {
      const itemsSnap = await getDocs(query(collection(firestore, 'ORDERS', orderId, 'OrderItems')));
      const items = [];
      itemsSnap.forEach(d => items.push(d.data()));
      const { orderStatus, paymentStatus } = aggregateOrderMeta(items);
      const parentRef = doc(firestore, 'ORDERS', orderId);
      await updateDoc(parentRef, {
        Order_Status: orderStatus,
        Payment_Status: paymentStatus
      });
    } catch (e) {
      console.warn('Parent order update skipped/failed:', e?.message || e);
    }
  };

  // Helper: apply local UI update immediately
  const applyLocalCancellation = (orderId, productId) => {
    setOrders(prev => {
      const next = (prev || []).map(o => {
        if (o.id !== orderId) return o;
        const updatedItems = (o.items || []).map(it => it.Product_Id === productId ? {
          ...it,
          Cancellation_Requested: true,
          Order_Status: 'Cancelled'
        } : it);
        const { orderStatus, paymentStatus } = aggregateOrderMeta(updatedItems);
        return { ...o, items: updatedItems, Order_Status: orderStatus, Payment_Status: paymentStatus };
      });
      return next;
    });
    setSelectedOrder(prev => {
      if (!prev || prev.id !== orderId) return prev;
      const updatedItems = (prev.items || []).map(it => it.Product_Id === productId ? {
        ...it,
        Cancellation_Requested: true,
        Order_Status: 'Cancelled'
      } : it);
      const { orderStatus, paymentStatus } = aggregateOrderMeta(updatedItems);
      return { ...prev, items: updatedItems, Order_Status: orderStatus, Payment_Status: paymentStatus };
    });
  };

  useEffect(() => {
    console.log('OrdersScreen useEffect - userInfo:', userInfo);
    // Try multiple ways to get user ID
    const userId = userInfo?.uid || userInfo?.id || firebaseAuth.currentUser?.uid;
    if (userId) {
      console.log('User ID found:', userId);
      loadUserOrders();
    } else {
      console.log('No user ID found, userInfo:', userInfo);
      console.log('Firebase Auth current user:', firebaseAuth.currentUser);
      setLoading(false);
    }
  }, [userInfo]);

  const loadUserOrders = async () => {
    try {
      setLoading(true);
      // Try multiple ways to get user ID, including Firebase Auth fallback
      const userId = userInfo?.uid || userInfo?.id || firebaseAuth.currentUser?.uid;
      console.log('Loading orders for user:', userId);
      console.log('Full userInfo:', userInfo);
      
      // Follow the exact Android pattern: USERS/{uid}/USER_ORDERS -> ORDERS/{orderId}/OrderItems
      const userOrdersRef = collection(firestore, 'USERS', userId, 'USER_ORDERS');
      console.log('Querying path:', `USERS/${userId}/USER_ORDERS`);
      const q = query(userOrdersRef, orderBy('Time', 'desc'));
      const userOrdersSnap = await getDocs(q);
      
      console.log('Found user order references:', userOrdersSnap.size);
      
      if (userOrdersSnap.empty) {
        console.log('No orders found in USER_ORDERS subcollection');
        console.log('Trying alternative: Query ORDERS collection directly by User_Id');
        
        // Fallback: Query ORDERS collection directly
        const ordersRef = collection(firestore, 'ORDERS');
        const ordersQuery = query(ordersRef, where('User_Id', '==', userId));
        const ordersSnap = await getDocs(ordersQuery);
        
        console.log('Found orders in ORDERS collection:', ordersSnap.size);
        
        if (ordersSnap.empty) {
          setOrders([]);
          return;
        }
        
        // Process orders found directly
        const allOrderItems = [];
        const orderPromises = [];
        
        ordersSnap.forEach((orderDoc) => {
          const orderId = orderDoc.id;
          console.log('Processing order from ORDERS collection:', orderId);
          
          const orderItemsPromise = getDocs(
            query(collection(firestore, 'ORDERS', orderId, 'OrderItems'), orderBy('Ordered_Date'))
          ).then(orderItemsSnap => {
            const orderItems = [];
            orderItemsSnap.forEach(itemDoc => {
              const itemData = itemDoc.data();
              orderItems.push({
                id: itemDoc.id,
                orderId: orderId,
                Product_Id: itemData.Product_Id,
                Order_Status: itemData.Order_Status,
                Address: itemData.Address,
                Coupon_Id: itemData.Coupon_Id,
                Cutted_Price: itemData.Cutted_Price,
                Ordered_Date: itemData.Ordered_Date,
                Packed_Date: itemData.Packed_Date,
                Shipped_Date: itemData.Shipped_Date,
                Delivered_Date: itemData.Delivered_Date,
                Cancelled_Date: itemData.Cancelled_Date,
                Discounted_Price: itemData.Discounted_Price,
                Free_Coupons: itemData.Free_Coupons,
                FullName: itemData.FullName,
                Order_Id: itemData.Order_Id,
                Payment_Method: itemData.Payment_Method,
                Pincode: itemData.Pincode,
                Product_price: itemData.Product_price,
                Product_Quantity: itemData.Product_Quantity,
                User_Id: itemData.User_Id,
                Mobile_No: itemData.Mobile_No,
                Product_Image: itemData.Product_Image,
                Product_Title: itemData.Product_Title,
                Delivery_Price: itemData.Delivery_Price,
                Cancellation_Requested: itemData.Cancellation_Requested,
                Return_Requested: itemData.Return_Requested,
                Total_Weight: itemData.Total_Weight,
                Seller_Id: itemData.Seller_Id,
                Return_Status: itemData.Return_Status,
                isCompanyDelivery: itemData.isCompanyDelivery,
                Delivery_Code: itemData.Delivery_Code,
                isBankDetailAvailable: itemData.isBankDetailAvailable,
                fixedShippingCharges: itemData.fixedShippingCharges,
                Txn_Discount: itemData.Txn_Discount,
                Notes: itemData.Notes,
                CustTransaction_No: itemData.CustTransaction_No,
                CustUTR_No: itemData.CustUTR_No,
                RefundCustTxnNo: itemData.RefundCustTxnNo,
                RefundCustUTR_No: itemData.RefundCustUTR_No
              });
            });
            return orderItems;
          }).catch(err => {
            console.error('Error loading items for order:', orderId, err);
            return [];
          });
          
          orderPromises.push(orderItemsPromise);
        });
        
        const orderItemsArrays = await Promise.all(orderPromises);
        orderItemsArrays.forEach(items => {
          allOrderItems.push(...items);
        });
        
        console.log('Total order items loaded from ORDERS:', allOrderItems.length);
        
        // Group items by Order_Id to create order objects
        const ordersMap = new Map();
        allOrderItems.forEach(item => {
          const orderId = item.Order_Id || item.orderId;
          if (!ordersMap.has(orderId)) {
            ordersMap.set(orderId, {
              id: orderId,
              Order_Status: item.Order_Status,
              Order_Date: item.Ordered_Date,
              Payment_Method: item.Payment_Method,
              Payment_Status: 'Not Paid',
              Total_Amount: 0,
              Total_Item_Price: 0,
              Delivery_Price: item.Delivery_Price || 0,
              items: []
            });
          }

          const order = ordersMap.get(orderId);
          order.items.push(item);

          const priority = {
            'delivered': 4,
            'shipped': 3,
            'packed': 2,
            'ordered': 1,
            'cancelled': 0
          };
          const currentStatus = (order.Order_Status || '').toLowerCase();
          const newStatus = (item.Order_Status || '').toLowerCase();
          if (!currentStatus || (priority[newStatus] ?? -1) > (priority[currentStatus] ?? -1)) {
            order.Order_Status = item.Order_Status;
          }

          const itemPrice = parseFloat(item.Product_price || 0) * parseInt(item.Product_Quantity || 1);
          order.Total_Item_Price += itemPrice;
          order.Total_Amount = order.Total_Item_Price + (order.Delivery_Price || 0);
        });
        
        const ordersArray = Array.from(ordersMap.values()).map(o => ({
          ...o,
          Payment_Status: determinePaymentStatusFromOrder(o)
        })).sort((a, b) => {
          const dateA = a.Order_Date?.toDate ? a.Order_Date.toDate() : new Date(a.Order_Date || 0);
          const dateB = b.Order_Date?.toDate ? b.Order_Date.toDate() : new Date(b.Order_Date || 0);
          return dateB - dateA;
        });

        console.log('Final orders from ORDERS collection:', ordersArray.length);
        setOrders(ordersArray);
        return ordersArray;
      }
      
      // Process each order reference to get order items (matching Android loadOrders method)
      const allOrderItems = [];
      const orderPromises = [];
      
      userOrdersSnap.forEach((userOrderDoc) => {
        const orderId = userOrderDoc.data().order_id || userOrderDoc.id;
        console.log('Processing order:', orderId);
        
        // Get OrderItems from ORDERS/{orderId}/OrderItems (exactly like Android)
        const orderItemsPromise = getDocs(
          query(collection(firestore, 'ORDERS', orderId, 'OrderItems'), orderBy('Ordered_Date'))
        ).then(orderItemsSnap => {
          const orderItems = [];
          orderItemsSnap.forEach(itemDoc => {
            const itemData = itemDoc.data();
            orderItems.push({
              id: itemDoc.id,
              orderId: orderId,
              // Map all the fields from Android MyOrderItemModel
              Product_Id: itemData.Product_Id,
              Order_Status: itemData.Order_Status,
              Address: itemData.Address,
              Coupon_Id: itemData.Coupon_Id,
              Cutted_Price: itemData.Cutted_Price,
              Ordered_Date: itemData.Ordered_Date,
              Packed_Date: itemData.Packed_Date,
              Shipped_Date: itemData.Shipped_Date,
              Delivered_Date: itemData.Delivered_Date,
              Cancelled_Date: itemData.Cancelled_Date,
              Discounted_Price: itemData.Discounted_Price,
              Free_Coupons: itemData.Free_Coupons,
              FullName: itemData.FullName,
              Order_Id: itemData.Order_Id,
              Payment_Method: itemData.Payment_Method,
              Pincode: itemData.Pincode,
              Product_price: itemData.Product_price,
              Product_Quantity: itemData.Product_Quantity,
              User_Id: itemData.User_Id,
              Mobile_No: itemData.Mobile_No,
              Product_Image: itemData.Product_Image,
              Product_Title: itemData.Product_Title,
              Delivery_Price: itemData.Delivery_Price,
              Cancellation_Requested: itemData.Cancellation_Requested,
              Return_Requested: itemData.Return_Requested,
              Total_Weight: itemData.Total_Weight,
              Seller_Id: itemData.Seller_Id,
              Return_Status: itemData.Return_Status,
              isCompanyDelivery: itemData.isCompanyDelivery,
              Delivery_Code: itemData.Delivery_Code,
              isBankDetailAvailable: itemData.isBankDetailAvailable,
              fixedShippingCharges: itemData.fixedShippingCharges,
              Txn_Discount: itemData.Txn_Discount,
              Notes: itemData.Notes,
              CustTransaction_No: itemData.CustTransaction_No,
              CustUTR_No: itemData.CustUTR_No,
              RefundCustTxnNo: itemData.RefundCustTxnNo,
              RefundCustUTR_No: itemData.RefundCustUTR_No
            });
          });
          return orderItems;
        }).catch(err => {
          console.error('Error loading items for order:', orderId, err);
          return [];
        });
        
        orderPromises.push(orderItemsPromise);
      });
      
      // Wait for all order items to load
      const orderItemsArrays = await Promise.all(orderPromises);
      
      // Flatten all order items into single array (like Android myOrderItemModelList)
      orderItemsArrays.forEach(items => {
        allOrderItems.push(...items);
      });
      
      console.log('Total order items loaded:', allOrderItems.length);
      
      // Group items by Order_Id to create order objects
      const ordersMap = new Map();
      allOrderItems.forEach(item => {
        const orderId = item.Order_Id || item.orderId;
        if (!ordersMap.has(orderId)) {
          ordersMap.set(orderId, {
            id: orderId,
            Order_Status: item.Order_Status,
            Order_Date: item.Ordered_Date,
            Payment_Method: item.Payment_Method,
            Payment_Status: 'Not Paid',
            Total_Amount: 0,
            Total_Item_Price: 0,
            Delivery_Price: item.Delivery_Price || 0,
            items: []
          });
        }

        const order = ordersMap.get(orderId);
        order.items.push(item);

        // Keep the most advanced status for the order if items differ
        // Priority order: delivered > shipped > packed > ordered > cancelled
        const priority = {
          'delivered': 4,
          'shipped': 3,
          'packed': 2,
          'ordered': 1,
          'cancelled': 0
        };
        const currentStatus = (order.Order_Status || '').toLowerCase();
        const newStatus = (item.Order_Status || '').toLowerCase();
        if (!currentStatus || (priority[newStatus] ?? -1) > (priority[currentStatus] ?? -1)) {
          order.Order_Status = item.Order_Status;
        }

        // Calculate totals
        const itemPrice = parseFloat(item.Product_price || 0) * parseInt(item.Product_Quantity || 1);
        order.Total_Item_Price += itemPrice;
        order.Total_Amount = order.Total_Item_Price + (order.Delivery_Price || 0);
      });
      // Compute payment status using items and then sort by date
      const ordersArray = Array.from(ordersMap.values()).map(o => ({
        ...o,
        Payment_Status: determinePaymentStatusFromOrder(o)
      })).sort((a, b) => {
        const dateA = a.Order_Date?.toDate ? a.Order_Date.toDate() : new Date(a.Order_Date || 0);
        const dateB = b.Order_Date?.toDate ? b.Order_Date.toDate() : new Date(b.Order_Date || 0);
        return dateB - dateA;
      });

      console.log('Final orders:', ordersArray.length);
      setOrders(ordersArray);
      return ordersArray;
      
    } catch (error) {
      console.error('Error loading orders:', error);
      toastError('Failed to load orders: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'ordered': return '#2196F3';
      case 'packed': return '#FF9800';
      case 'shipped': return '#9C27B0';
      case 'delivered': return '#4CAF50';
      case 'cancelled': return '#F44336';
      default: return '#757575';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'ordered': return '📦';
      case 'packed': return '📋';
      case 'shipped': return '🚚';
      case 'delivered': return '✅';
      case 'cancelled': return '❌';
      default: return '⏳';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-IN', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return 'Invalid Date';
    }
  };

  const showOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedOrder(null);
    setCancelingItemId(null);
    setIsBulkCanceling(false);
    setShowCancelConfirm(false);
    setShowBulkCancelConfirm(false);
    setPendingCancelItem(null);
  };

  // Cancel a specific order item (allowed when status is Ordered or Packed and not already requested)
  const handleCancelItem = async (item) => {
    try {
      const canCancel = ['ordered', 'packed'].includes((item.Order_Status || '').toLowerCase()) && !item.Cancellation_Requested;
      if (!canCancel) {
        toastError('This item cannot be cancelled now.');
        return;
      }
      setCancelingItemId(item.Product_Id);
      const orderId = item.orderId || item.Order_Id || selectedOrder?.id;
      if (!orderId) {
        toastError('Order id not found for cancellation.');
        setCancelingItemId(null);
        return;
      }

      // 1) Log to CANCELLED ORDERS
      const cancelledRef = doc(firestore, 'CANCELLED ORDERS', orderId);
      await setDoc(cancelledRef, {
        Order_Id: orderId,
        Product_Id: item.Product_Id,
        Cancellation_Request_Date: serverTimestamp(),
        Order_Cancelled: true,
      }, { merge: true });

      // 2) Update the order item status
      const itemRef = doc(firestore, 'ORDERS', orderId, 'OrderItems', item.Product_Id);
      await updateDoc(itemRef, {
        Cancellation_Requested: true,
        Order_Status: 'Cancelled',
        Cancelled_Date: serverTimestamp(),
        Notes: 'Cancelled by user.'
      });

      // Update parent order doc and local UI immediately
      await updateParentOrderDoc(orderId);
      applyLocalCancellation(orderId, item.Product_Id);
      toastSuccess('Cancellation requested.');
      setCancelingItemId(null);
    } catch (e) {
      console.error('Cancel item failed:', e);
      toastError('Failed to request cancellation: ' + (e?.message || 'Unknown error'));
      setCancelingItemId(null);
    }
  };

  // Bulk cancel when all items are eligible
  const handleCancelWholeOrder = async () => {
    if (!selectedOrder) return;
    const items = selectedOrder.items || [];
    const eligibleItems = items.filter(it => ['ordered','packed'].includes((it.Order_Status || '').toLowerCase()) && !it.Cancellation_Requested);
    if (eligibleItems.length !== items.length) {
      toastError('All items must be Ordered or Packed and not already requested.');
      return;
    }
    try {
      setIsBulkCanceling(true);
      const orderId = selectedOrder.id;
      // perform all item updates in parallel
      await Promise.all(eligibleItems.map(async (item) => {
        const cancelledRef = doc(firestore, 'CANCELLED ORDERS', orderId);
        await setDoc(cancelledRef, {
          Order_Id: orderId,
          Product_Id: item.Product_Id,
          Cancellation_Request_Date: serverTimestamp(),
          Order_Cancelled: true,
        }, { merge: true });
        const itemRef = doc(firestore, 'ORDERS', orderId, 'OrderItems', item.Product_Id);
        await updateDoc(itemRef, {
          Cancellation_Requested: true,
          Order_Status: 'Cancelled',
          Cancelled_Date: serverTimestamp(),
          Notes: 'Cancelled by user.'
        });
      }));

      await updateParentOrderDoc(orderId);
      // Apply local updates for all items
      setOrders(prev => {
        const next = (prev || []).map(o => {
          if (o.id !== orderId) return o;
          const updatedItems = (o.items || []).map(it => ({ ...it, Cancellation_Requested: true, Order_Status: 'Cancelled' }));
          const { orderStatus, paymentStatus } = aggregateOrderMeta(updatedItems);
          return { ...o, items: updatedItems, Order_Status: orderStatus, Payment_Status: paymentStatus };
        });
        return next;
      });
      setSelectedOrder(prev => {
        if (!prev || prev.id !== orderId) return prev;
        const updatedItems = (prev.items || []).map(it => ({ ...it, Cancellation_Requested: true, Order_Status: 'Cancelled' }));
        const { orderStatus, paymentStatus } = aggregateOrderMeta(updatedItems);
        return { ...prev, items: updatedItems, Order_Status: orderStatus, Payment_Status: paymentStatus };
      });
      toastSuccess('Order cancellation requested.');
    } catch (e) {
      console.error('Bulk cancel failed:', e);
      toastError('Failed to cancel order: ' + (e?.message || 'Unknown error'));
    } finally {
      setIsBulkCanceling(false);
      setShowBulkCancelConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="orders-screen">
        <div className="orders-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading your orders...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!userInfo) {
    return (
      <div className="orders-screen">
        <div className="orders-container">
          <div className="empty-state">
            <div className="empty-icon">🔒</div>
            <h3>Please Sign In</h3>
            <p>You need to be logged in to view your orders.</p>
            <a href="/signin" className="btn btn-primary">Sign In</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-screen">
      <div className="orders-container">
        <div className="orders-header">
          <h1>📋 Your Orders</h1>
          <p>Track and manage your order history</p>
        </div>

        {orders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>No Orders Yet</h3>
            <p>You haven't placed any orders. Start shopping to see your orders here!</p>
            <a href="/" className="btn btn-primary">Start Shopping</a>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map((order) => (
              <div key={order.id} className="order-card" onClick={() => showOrderDetails(order)}>
                <div className="order-header">
                  <div className="order-info">
                    <h3>Order #{order.id}</h3>
                    <p className="order-date">{formatDate(order.Order_Date)}</p>
                  </div>
                  <div className="order-status">
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(order.Order_Status) }}
                    >
                      {getStatusIcon(order.Order_Status)} {order.Order_Status || 'Processing'}
                    </span>
                  </div>
                </div>
                
                <div className="order-summary">
                  <div className="order-items">
                    {order.items?.slice(0, 2).map((item, index) => (
                      <div key={index} className="order-item-preview">
                        <img 
                          src={item.Product_Image || '/placeholder-product.png'} 
                          alt={item.Product_Title}
                          className="item-image"
                        />
                        <div className="item-details">
                          <p className="item-title">{item.Product_Title}</p>
                          <p className="item-quantity">Qty: {item.Product_Quantity}</p>
                        </div>
                      </div>
                    ))}
                    {order.items?.length > 2 && (
                      <div className="more-items">
                        +{order.items.length - 2} more item(s)
                      </div>
                    )}
                  </div>
                  
                  <div className="order-total">
                    <div className="payment-info">
                      <p className="payment-method">{order.Payment_Method}</p>
                      <p className="payment-status" style={{ 
                        color: order.Payment_Status === 'Paid' ? '#4CAF50' : '#FF9800' 
                      }}>
                        {order.Payment_Status === 'Paid' ? '✅ Paid' : '⏳ Pending'}
                      </p>
                    </div>
                    <div className="total-amount">
                      <span className="amount">₹{Number(order.Total_Amount || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showDetails && selectedOrder && (
        <div className="order-details-modal" onClick={closeDetails}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Order Details</h2>
              <button className="close-btn" onClick={closeDetails}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="order-info-section">
                <h3>Order Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Order ID:</label>
                    <span>{selectedOrder.id}</span>
                  </div>
                  <div className="info-item">
                    <label>Order Date:</label>
                    <span>{formatDate(selectedOrder.Order_Date)}</span>
                  </div>
                  <div className="info-item">
                    <label>Status:</label>
                    <span style={{ color: getStatusColor(selectedOrder.Order_Status) }}>
                      {getStatusIcon(selectedOrder.Order_Status)} {selectedOrder.Order_Status}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Payment Method:</label>
                    <span>{selectedOrder.Payment_Method}</span>
                  </div>
                  <div className="info-item">
                    <label>Payment Status:</label>
                    <span style={{ color: selectedOrder.Payment_Status === 'Paid' ? '#4CAF50' : '#FF9800' }}>
                      {selectedOrder.Payment_Status === 'Paid' ? '✅ Paid' : '⏳ Pending'}
                    </span>
                  </div>
                  {selectedOrder.CustTransaction_No && (
                    <div className="info-item">
                      <label>Transaction ID:</label>
                      <span>{selectedOrder.CustTransaction_No}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="order-items-section">
                <h3>Order Items</h3>
                <div className="items-list">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="item-detail">
                      <img 
                        src={item.Product_Image || '/placeholder-product.png'} 
                        alt={item.Product_Title}
                        className="item-image-large"
                      />
                      <div className="item-info">
                        <h4>{item.Product_Title}</h4>
                        <p>Quantity: {item.Product_Quantity}</p>
                        <p>Price: ₹{Number(item.Product_price || 0).toFixed(2)}</p>
                        {item.Cutted_Price && (
                          <p className="original-price">Original: ₹{Number(item.Cutted_Price).toFixed(2)}</p>
                        )}
                      </div>
                      <div className="item-actions">
                        {(['ordered','packed'].includes((item.Order_Status || '').toLowerCase()) && !item.Cancellation_Requested) ? (
                          <button className="cancel-btn" disabled={isBulkCanceling || cancelingItemId === item.Product_Id} onClick={() => { setPendingCancelItem(item); setShowCancelConfirm(true); }}>
                            {cancelingItemId === item.Product_Id ? 'Cancelling...' : 'Cancel item'}
                          </button>
                        ) : item.Cancellation_Requested ? (
                          <span className="cancel-status">Cancellation in process</span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="order-total-section">
                <h3>Order Summary</h3>
                {/* Order-level actions */}
                {(() => {
                  const items = selectedOrder.items || [];
                  const allEligible = items.length > 0 && items.every(it => ['ordered','packed'].includes((it.Order_Status || '').toLowerCase()) && !it.Cancellation_Requested);
                  return allEligible ? (
                    <div className="order-actions">
                      <button className="cancel-order-btn" disabled={isBulkCanceling} onClick={() => setShowBulkCancelConfirm(true)}>
                        {isBulkCanceling ? 'Cancelling order...' : 'Cancel entire order'}
                      </button>
                    </div>
                  ) : null;
                })()}
                <div className="total-breakdown">
                  <div className="total-row">
                    <span>Items Total:</span>
                    <span>₹{Number(selectedOrder.Total_Item_Price || 0).toFixed(2)}</span>
                  </div>
                  {selectedOrder.Delivery_Price > 0 && (
                    <div className="total-row">
                      <span>Delivery:</span>
                      <span>₹{Number(selectedOrder.Delivery_Price).toFixed(2)}</span>
                    </div>
                  )}
                  {selectedOrder.Saved_Amount > 0 && (
                    <div className="total-row savings">
                      <span>Savings:</span>
                      <span>-₹{Number(selectedOrder.Saved_Amount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="total-row final">
                    <span>Total Amount:</span>
                    <span>₹{Number(selectedOrder.Total_Amount || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Confirm modals */}
      {showCancelConfirm && pendingCancelItem && (
        <div className="confirm-modal-backdrop" onClick={() => setShowCancelConfirm(false)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Cancel this item?</h4>
            <p>This action will request cancellation for this item.</p>
            <div className="confirm-actions">
              <button className="btn-secondary" onClick={() => setShowCancelConfirm(false)}>No</button>
              <button className="btn-primary" onClick={async () => { setShowCancelConfirm(false); await handleCancelItem(pendingCancelItem); setPendingCancelItem(null); }}>Yes, cancel</button>
            </div>
          </div>
        </div>
      )}

      {showBulkCancelConfirm && selectedOrder && (
        <div className="confirm-modal-backdrop" onClick={() => setShowBulkCancelConfirm(false)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Cancel entire order?</h4>
            <p>All items will be requested for cancellation.</p>
            <div className="confirm-actions">
              <button className="btn-secondary" onClick={() => setShowBulkCancelConfirm(false)}>No</button>
              <button className="btn-primary" onClick={handleCancelWholeOrder} disabled={isBulkCanceling}>{isBulkCanceling ? 'Cancelling...' : 'Yes, cancel order'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrdersScreen;
